import React, { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import { FaClipboardList } from "react-icons/fa";
import { useFormContext } from "../../contexts/FormContext";
import { Modal, Button } from "react-bootstrap";
import useCommunityNotifications from "../../hooks/useCommunityNotifications";
import usePendingListingsCount from "../../hooks/usePendingListingsCount";

export default function LeftMenu({ onSidebarToggle }) {
  const Navigate = useNavigate();
  // Use shared userDetails from AuthContext (hydrated from localStorage)
  // so the sidebar renders instantly instead of waiting on a duplicate fetch.
  const { logout, userDetails } = useAuth();
  const currentUser = userDetails;
  const { isFormDirty, formType, formMessage } = useFormContext();
  const { unreadCount: communityUnreadCount } = useCommunityNotifications();
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState(null);
  const [isWaiversOpen, setIsWaiversOpen] = useState(false);
  const [isBlogOpen, setIsBlogOpen] = useState(false);
  const [isCommunityOpen, setIsCommunityOpen] = useState(false);

  const location = useLocation();
  const pendingListingsCount = usePendingListingsCount();

  const handleLogout = async () => {
    try {
      if (isFormDirty && (formType === 'boat' || formType === 'payment')) {
        setPendingNavigation({ type: 'logout' });
        setShowConfirmModal(true);
      } else {
        await logout();
        console.log("Logged Out");
        Navigate("/");
      }
    } catch {
      console.log("Failed to log out");
    }
  };

  const handleConfirmNavigation = async () => {
    setShowConfirmModal(false);
    if (pendingNavigation) {
      if (pendingNavigation.type === 'logout') {
        await logout();
        Navigate("/");
      } else if (pendingNavigation.type === 'link') {
        Navigate(pendingNavigation.to);
      }
      setPendingNavigation(null);
    }
  };

  const handleCancelNavigation = () => {
    setShowConfirmModal(false);
    setPendingNavigation(null);
  };

  const handleLinkClick = (to, e) => {
    if (isFormDirty && (formType === 'boat' || formType === 'payment')) {
      e.preventDefault();
      setPendingNavigation({ type: 'link', to });
      setShowConfirmModal(true);
      if (isMobileDevice()) {
        onSidebarToggle();
      }
      return false;
    }
    return true;
  };

  const openSidebar = () => {
    console.log("Clicked");
    if (isMobileDevice()) {
      onSidebarToggle();
    }
  };

  const isMobileDevice = () => {
    return window.innerWidth <= 768; // Adjust the threshold as needed
  };

  const toggleWaivers = () => {
    setIsWaiversOpen(!isWaiversOpen);
  };

  const toggleBlog = () => {
    setIsBlogOpen(!isBlogOpen);
  };

  const toggleCommunity = (e) => {
    e.preventDefault();
    setIsCommunityOpen(!isCommunityOpen);
  };

  return (
    <>
      {currentUser && (
        <>
          <div className="d-flex justify-content-end mobile-only">
            <Link
              className="nav-link hide-sidebar-toggle-button"
              onClick={openSidebar}
            >
              <i className="material-icons">close</i>
            </Link>
          </div>
          <ul className="accordion-menu">
            <li className="sidebar-title">Apps</li>
            <li onClick={openSidebar}>
              <Link
                to="/dashboard"
                className={location.pathname === "/dashboard" ? "active" : ""}
                onClick={(e) => handleLinkClick('/dashboard', e)}
              >
                <i className="material-icons-two-tone">dashboard</i>{" "}
                <span>Dashboard</span>
              </Link>
            </li>
            <li onClick={openSidebar}>
              <Link
                to="/dashboard/setting"
                className={
                  location.pathname === "/dashboard/setting" ? "active" : ""
                }
                onClick={(e) => handleLinkClick('/dashboard/setting', e)}
              >
                <i className="material-icons-two-tone">settings</i>
                <span>Settings</span>
              </Link>
            </li>
            <li onClick={openSidebar} className="mobile-only">
              <Link
                to="/dashboard/inbox"
                className={
                  location.pathname === "/dashboard/inbox" ? "active" : ""
                }
                onClick={(e) => handleLinkClick('/dashboard/inbox', e)}
              >
                <i className="material-icons-two-tone">forum</i>
                <span>Inbox</span>
              </Link>
            </li>
            <li onClick={openSidebar} className="mobile-only">
              <Link
                to="/dashboard/payments"
                className={
                  location.pathname === "/dashboard/payments" ? "active" : ""
                }
                onClick={(e) => handleLinkClick('/dashboard/payments', e)}
              >
                <i className="material-icons-two-tone">local_atm</i>
                <span>Payments</span>
              </Link>
            </li>

            {currentUser.role === "Boat Owner" && (
              <>
                <li className="sidebar-title">Listings</li>
                <li onClick={openSidebar}>
                  <Link
                    to="/dashboard/my-listings"
                    onClick={(e) => handleLinkClick('/dashboard/my-listings', e)}
                  >
                    <i className="material-icons-two-tone">directions_boat</i>
                    Boats
                    <i className="material-icons has-sub-menu">
                      keyboard_arrow_right
                    </i>
                  </Link>
                  <ul className="sub-menu">
                    <li>
                      <Link
                        to="/dashboard/my-listings"
                        className={
                          location.pathname === "/dashboard/my-listings"
                            ? "active"
                            : ""
                        }
                        onClick={(e) => handleLinkClick('/dashboard/my-listings', e)}
                      >
                        <span>My Boats</span>
                      </Link>
                    </li>
                    <li>
                      <Link
                        to="/dashboard/add-listing"
                        className={
                          location.pathname === "/dashboard/add-listing"
                            ? "active"
                            : ""
                        }
                        onClick={(e) => handleLinkClick('/dashboard/add-listing', e)}
                      >
                        <span>Add Boat</span>
                      </Link>
                    </li>
                  </ul>
                </li>
                <li onClick={openSidebar}>
                  <Link
                    to="/dashboard/pro-staff"
                    className={
                      location.pathname === "/dashboard/pro-staff"
                        ? "active"
                        : ""
                    }
                    onClick={(e) => handleLinkClick('/dashboard/pro-staff', e)}
                  >
                    <i className="material-icons-two-tone">verified</i>
                    <span>Become A Pro Staff</span>
                  </Link>
                </li>
                <li onClick={openSidebar}>
                  <Link
                    to="/dashboard/how"
                    className={
                      location.pathname === "/dashboard/how" ? "active" : ""
                    }
                    onClick={(e) => handleLinkClick('/dashboard/how', e)}
                  >
                    <i className="material-icons-two-tone">checklist</i>
                    <span>How It Works</span>
                  </Link>
                </li>
              </>
            )}
            {currentUser.role === "Renter" && (
              <>
                <li onClick={openSidebar}>
                  <Link
                    to="/dashboard/get-verified"
                    className={
                      location.pathname === "/dashboard/get-verified"
                        ? "active"
                        : ""
                    }
                  >
                    <i className="material-icons-two-tone">verified</i>
                    <span>Get Verified</span>
                  </Link>
                </li>
                <li className="sidebar-title">Bookings</li>
                <li onClick={openSidebar}>
                  <Link to="/dashboard/bookings">
                    <i className="material-icons-two-tone">calendar_month</i>
                    Bookings
                    <i className="material-icons has-sub-menu">
                      keyboard_arrow_right
                    </i>
                  </Link>
                  <ul className="sub-menu">
                    <li>
                      <Link
                        to="/dashboard/bookings"
                        className={
                          location.pathname === "/dashboard/bookings"
                            ? "active"
                            : ""
                        }
                      >
                        <span>My Bookings</span>
                      </Link>
                    </li>
                    <li>
                      <Link
                        to="/listings"
                        className={
                          location.pathname === "/listings" ? "active" : ""
                        }
                      >
                        <span>Find Boats</span>
                      </Link>
                    </li>
                  </ul>
                </li>
              </>
            )}
            {currentUser.role !== "Renter" && (
              <li onClick={openSidebar}>
                <Link
                  to="/dashboard/withdrawals"
                  className={
                    location.pathname === "/dashboard/withdrawals"
                      ? "active"
                      : ""
                  }
                >
                  <i className="material-icons-two-tone">payments</i>
                  <span>Withdrawals</span>
                </Link>
              </li>
            )}
            {currentUser.role === "Admin" && (
              <li onClick={openSidebar}>
                <Link
                  to="/dashboard/payments-dashboard"
                  className={
                    location.pathname === "/dashboard/payments-dashboard" ||
                    location.pathname.startsWith("/dashboard/payments-dashboard/")
                      ? "active" //hidden for now
                      : ""
                  }
                >
                  <i className="material-icons-two-tone">account_balance_wallet</i>
                  <span>Payments Dashboard</span>
                </Link>
              </li>
            )}
            {currentUser.role !== "Boat Owner" &&
              currentUser.role !== "Renter" && (
                // Default menu items for other roles
                <>
                  <li onClick={openSidebar}>
                    <Link
                      to="/dashboard/all-listings"
                      className={
                        location.pathname === "/dashboard/all-listings"
                          ? "active"
                          : ""
                      }
                    >
                      <FaClipboardList
                        size={24}
                        style={{
                          marginRight: "13px",
                          filter:
                            "invert(41%) sepia(17%) saturate(674%) hue-rotate(182deg) brightness(95%) contrast(89%)",
                        }}
                      />
                      {/* <i className="material-icons-two-tone">pending_actions</i> */}
                      <span>All Listings</span>
                    </Link>
                  </li>
                  <li onClick={openSidebar}>
                    <Link
                      to="/dashboard/pending-listings"
                      className={
                        location.pathname === "/dashboard/pending-listings"
                          ? "active"
                          : ""
                      }
                      style={{
                        display: "flex",
                        alignItems: "center",
                      }}
                    >
                      <i className="material-icons-two-tone">pending_actions</i>
                      <span style={{ flex: 1 }}>Pending Listings</span>
                      {pendingListingsCount > 0 && (
                        <span
                          className="sidebar-menu-count-badge"
                          aria-label={`${pendingListingsCount} pending listings`}
                        >
                          {pendingListingsCount > 99
                            ? "99+"
                            : pendingListingsCount}
                        </span>
                      )}
                    </Link>
                  </li>
                  <li onClick={openSidebar}>
                    <Link
                      to="/dashboard/rejected-listings"
                      className={
                        location.pathname === "/dashboard/rejected-listings"
                          ? "active"
                          : ""
                      }
                    >
                      <i className="material-icons-two-tone">block</i>
                      <span>Rejected Listings</span>
                    </Link>
                  </li>
                  <li onClick={openSidebar}>
                    <Link
                      to="/dashboard/users"
                      className={
                        location.pathname === "/dashboard/users" ? "active" : ""
                      }
                    >
                      <i className="material-icons-two-tone">group</i>
                      <span>All Renters</span>
                    </Link>
                  </li>
                  <li onClick={openSidebar}>
                    <Link
                      to="/dashboard/pending-renters"
                      className={
                        location.pathname === "/dashboard/pending-renters"
                          ? "active"
                          : ""
                      }
                    >
                      <i className="material-icons-two-tone">person_search</i>
                      <span>Pending Renters</span>
                    </Link>
                  </li>

                  <li onClick={openSidebar}>
                    <Link
                      to="/dashboard/boat-owners"
                      className={
                        location.pathname === "/dashboard/boat-owners"
                          ? "active"
                          : ""
                      }
                    >
                      <i className="material-icons-two-tone">
                        directions_boat_filled
                      </i>
                      <span>All Boat Owners</span>
                    </Link>
                  </li>

                  <li onClick={openSidebar}>
                    <Link
                      to="/dashboard/admin-messages"
                      className={
                        location.pathname === "/dashboard/admin-messages"
                          ? "active"
                          : ""
                      }
                    >
                      <i className="material-icons-two-tone">forum</i>
                      <span>Global Inbox</span>
                    </Link>
                  </li>

                  {/* <li onClick={openSidebar}>
                <Link to="/dashboard/pending-boat-owners" className={location.pathname === '/dashboard/pending-boat-owners' ? 'active' : ''}>
                  <i className="material-icons-two-tone">work_history</i><span>Pending Boat Owners</span>
                </Link>
              </li> */}

                  <li onClick={openSidebar}>
                    <Link
                      to="/dashboard/pending-pro-members"
                      className={
                        location.pathname === "/dashboard/pending-pro-members"
                          ? "active"
                          : ""
                      }
                    >
                      <i className="material-icons-two-tone">work_history</i>
                      <span>Pro Member Requests</span>
                    </Link>
                  </li>

                  <li onClick={openSidebar}>
                    <Link
                      to="/dashboard/coupons"
                      className={
                        location.pathname === "/dashboard/coupons"
                          ? "active"
                          : ""
                      }
                    >
                      <i className="material-icons-two-tone">discount</i>
                      <span>Coupons</span>
                    </Link>
                  </li>

                  <li onClick={openSidebar}>
                    <Link
                      to="/dashboard/referral-codes"
                      className={
                        location.pathname === "/dashboard/referral-codes"
                          ? "active"
                          : ""
                      }
                    >
                      <i className="material-icons-two-tone">card_giftcard</i>
                      <span>Referral Codes</span>
                    </Link>
                  </li>

                  {/* <li>
                <Link to="/dashboard/disputes">
                  <i className="material-icons-two-tone">report</i>Disputes
                </Link>
              </li> */}

                  <li className={isWaiversOpen ? "open" : ""}>
                    <Link
                      to="#"
                      onClick={toggleWaivers}
                    >
                      <i className="material-icons-two-tone">draw</i>Waivers
                      <i className="material-icons has-sub-menu">
                        keyboard_arrow_right
                      </i>
                    </Link>
                    <ul className="sub-menu" style={{ display: isWaiversOpen ? 'block' : 'none' }}>
                      <li onClick={openSidebar}>
                        <Link
                          onClick={openSidebar}
                          to="/dashboard/waivers"
                          className={
                            location.pathname === "/dashboard/waivers"
                              ? "active"
                              : ""
                          }
                        >
                          <span>All Waivers</span>
                        </Link>
                      </li>
                      <li onClick={openSidebar}>
                        <Link
                          onClick={openSidebar}
                          to="/dashboard/add-waiver"
                          className={
                            location.pathname === "/dashboard/add-waiver"
                              ? "active"
                              : ""
                          }
                        >
                          <span>Add Waiver</span>
                        </Link>
                      </li>
                    </ul>
                  </li>

                  <li className={isBlogOpen ? "open" : ""}>
                    <Link
                      to="#"
                      onClick={toggleBlog}
                    >
                      <i className="material-icons-two-tone">article</i>Blog
                      <i className="material-icons has-sub-menu">
                        keyboard_arrow_right
                      </i>
                    </Link>
                    <ul className="sub-menu" style={{ display: isBlogOpen ? 'block' : 'none' }}>
                      <li onClick={openSidebar}>
                        <Link
                          to="/dashboard/blog"
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <span>All Posts</span>
                        </Link>
                      </li>
                      <li onClick={openSidebar}>
                        <Link
                          to="/dashboard/blog/create"
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <span>New Post</span>
                        </Link>
                      </li>
                    </ul>
                  </li>

                  <li onClick={openSidebar}>
                    <Link
                      to="/dashboard/qr-analytics"
                      className={
                        location.pathname === "/dashboard/qr-analytics"
                          ? "active"
                          : ""
                      }
                    >
                      <i className="material-icons-two-tone">qr_code_scanner</i>
                      <span>QR Analytics</span>
                    </Link>
                  </li>
                </>
              )}
            {currentUser.role === "Admin" ? (
              <li className={isCommunityOpen ? "open" : ""}>
                <Link to="#" onClick={toggleCommunity}>
                  <i className="material-icons-two-tone">forum</i>Community
                  <i className="material-icons has-sub-menu">keyboard_arrow_right</i>
                </Link>
                <ul className="sub-menu" style={{ display: isCommunityOpen ? 'block' : 'none' }}>
                  <li onClick={openSidebar}>
                    <Link to="/dashboard/community" className={location.pathname === "/dashboard/community" ? "active" : ""}>
                      <span>My Profile</span>
                    </Link>
                  </li>
                  <li onClick={openSidebar}>
                    <Link to="/dashboard/community/moderation" className={location.pathname === "/dashboard/community/moderation" ? "active" : ""}>
                      <span>Moderation Queue</span>
                    </Link>
                  </li>
                  <li onClick={openSidebar}>
                    <Link to="/dashboard/community/all-discussions" className={location.pathname === "/dashboard/community/all-discussions" ? "active" : ""}>
                      <span>All Discussions</span>
                    </Link>
                  </li>
                  <li onClick={openSidebar}>
                    <Link to="/dashboard/community/manage" className={location.pathname === "/dashboard/community/manage" ? "active" : ""}>
                      <span>Channel Manager</span>
                    </Link>
                  </li>
                  <li onClick={openSidebar}>
                    <Link to="/dashboard/community/operator-reviews" className={location.pathname === "/dashboard/community/operator-reviews" ? "active" : ""}>
                      <span>Operator Reviews</span>
                    </Link>
                  </li>
                  <li onClick={openSidebar}>
                    <Link to="/dashboard/community/lakes-tournaments" className={location.pathname === "/dashboard/community/lakes-tournaments" ? "active" : ""}>
                      <span>Tournaments</span>
                    </Link>
                  </li>
                  <li onClick={openSidebar}>
                    <Link to="/dashboard/community/ai-config" className={location.pathname === "/dashboard/community/ai-config" ? "active" : ""}>
                      <span>AI Settings</span>
                    </Link>
                  </li>
                </ul>
              </li>
            ) : (
              <li onClick={openSidebar}>
                <Link
                  to="/dashboard/community"
                  className={location.pathname.includes("/dashboard/community") ? "active" : ""}
                  onClick={(e) => handleLinkClick('/dashboard/community', e)}
                >
                  <i className="material-icons-two-tone">forum</i>
                  Community
                  {communityUnreadCount > 0 && (
                    <span className="badge bg-danger rounded-pill ms-auto" style={{ fontSize: '0.7rem', marginLeft: 'auto' }}>
                      {communityUnreadCount > 99 ? '99+' : communityUnreadCount}
                    </span>
                  )}
                </Link>
              </li>
            )}
            <li className="mobile-only" onClick={openSidebar}>
              <Link
                to="/"
                className={location.pathname === "/" ? "active" : ""}
                onClick={(e) => handleLinkClick('/', e)}
              >
                <i className="material-icons-two-tone">language</i>
                <span>Back to Website</span>
              </Link>
            </li>
            <li>
              <Link onClick={handleLogout}>
                <i className="material-icons-two-tone">logout</i>Logout
              </Link>
            </li>
          </ul>
          {/* Confirmation Modal */}
          <Modal show={showConfirmModal} onHide={handleCancelNavigation} centered>
            <Modal.Header closeButton>
              <Modal.Title>{formType === 'payment' ? 'Pending Payment' : 'Unsaved Changes'}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              {formMessage || 'You have unsaved changes in your boat form. If you navigate away, all your data will be lost. Do you want to continue?'}
            </Modal.Body>
            <Modal.Footer>
              <Button variant="secondary" onClick={handleCancelNavigation}>
                Cancel
              </Button>
              <Button variant="primary" onClick={handleConfirmNavigation}>
                Continue
              </Button>
            </Modal.Footer>
          </Modal>
        </>
      )}
    </>
  );
}
